import { useMemo, useState } from 'react'
import { useMetaDataContext } from '../../../../../state-management/contexts/MetaDataContext'
import NameValueTable from '../../../../../components/ui-components/NameValueTable'
import { useEffect } from 'react'
import {
  convertEpochToDate,
  convertUnitAndValue,
  fixedDigitsAndFormatNumberWithCurrency,
  isDecimal,
  roundOffAndFormatNumberWithCurrency,
  toFixed
} from '../../../../../utils/utils'
import { EventNames, UNITS } from '../../../../../types/misc.types'
import { getChangeStyle } from '../../../../../utils/mutualFundDetail'

import { trackEvent } from '../../../../../utils/appBridge'
import HelpButton from '../../../../../components/ui-components/HelpButton'
import { MyInvestmentBottomSheet } from '../../../../../components/BottomSheetComponents/MyInvestmentBottomSheet'
import NameValueTableRow from '../../../../../components/ui-components/NameValueTableRow'
import InPlaceNameValueAccordion from '../../../../../components/ui-components/InPlaceNameValueAccordion'
import { useBottomSheet } from '../../../../../state-management/contexts/BottomSheetContext'
import { FolioBottomSheet } from '../../../../../components/BottomSheetComponents/FolioBottomSheet'
import { InvestmentDataType, PercentageReturn } from '../../../types/investmentApi.types'
import { TextToggle } from '../../../../../components/ui-components/TextToggle'
import { NameValueDatum } from '../../../../../types/mutual-fund-detail.types'
import { formatDigitsForSpeech } from '../../../../../utils/a11ySpeech'

const INVESTMENT_PERFORMANCE_SCREEN_NAME = `MF_L2_My_Investment_Performance`
const XIRR_TOGGLE_OPTIONS = ['XIRR', 'Lifetime XIRR']

export default function InvestmentDetails({ investmentDataOverride }: { investmentDataOverride: InvestmentDataType }) {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const [isInvestedAccordionOpen, setIsInvestedAccordionOpen] = useState(false)
  const [xirrToggleOption, setXirrToggleOption] = useState(XIRR_TOGGLE_OPTIONS[0])
  const { isin } = useMetaDataContext()
  const effectiveInvestmentData = investmentDataOverride

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        eventId: 'My_Investment',
        ScreenName: INVESTMENT_PERFORMANCE_SCREEN_NAME,
        fundName: isin,
        screenType: 'MF_L2'
      }
    })
  }, [])

  const totalInvested = useMemo(() => {
    return effectiveInvestmentData?.investedAmount?.amount ?? 0
  }, [effectiveInvestmentData])

  const returnsTableData = useMemo(
    () =>
      buildReturnsTableData({
        effectiveInvestmentData,
        xirrToggleOption,
        onXirrToggleClick: setXirrToggleOption
      }),
    [effectiveInvestmentData, xirrToggleOption]
  )

  const singleFolio = effectiveInvestmentData?.folioDetails?.[0]?.folioNumber

  return (
    <div className='flex flex-col gap-[16px] w-full'>
      <div className='flex justify-between items-center relative'>
        <h2 className='text-black font-[500] text-[14px] m-0'>Investment Details</h2>
        <div className='absolute top-0 right-0'>
          <HelpButton
            aria-label='Help about investment details'
            onClick={() => {
              openBottomSheet(
                <MyInvestmentBottomSheet onDismiss={closeBottomSheet} ScreenName={INVESTMENT_PERFORMANCE_SCREEN_NAME} />
              )
            }}
          />
        </div>
      </div>

      <div className='rounded-xl border border-[#EBF2F2] w-full p-4'>
        <div className='pb-2 w-full'>
          <NameValueTableRow
            name='Current Value'
            value={`${roundOffAndFormatNumberWithCurrency(effectiveInvestmentData?.currentGain?.amount ?? 0)}`}
            nameClassName='!text-[#3D3D3D]'
            valueClassName='!text-[#232323]'
            groupForAccessibility
          />
        </div>
        <div className='pb-2 w-full'>
          {effectiveInvestmentData?.investmentDetails?.puInvestmentAmount?.amount &&
            effectiveInvestmentData?.investmentDetails?.externalInvestmentAmount?.amount ? (
            <div
              style={{
                width: isInvestedAccordionOpen ? 'calc(100% + 24px)' : '100%',
                marginLeft: isInvestedAccordionOpen ? '-12px' : '0px'
              }}
            >
              <InPlaceNameValueAccordion
                name='Total Invested'
                value={`${roundOffAndFormatNumberWithCurrency(totalInvested)}`}
                alwaysShowValue={true}
                nameClassName='!text-[#3D3D3D]'
                valueClassName='!text-[#232323]'
                isExpanded={isInvestedAccordionOpen}
                onAccordionClick={() => {
                  setIsInvestedAccordionOpen((isInvestedAccordionOpen) => !isInvestedAccordionOpen)
                }}
              >
                <NameValueTable
                  data={[
                    {
                      name: 'PowerUp',
                      value: `${roundOffAndFormatNumberWithCurrency(effectiveInvestmentData?.investmentDetails?.puInvestmentAmount?.amount ?? 0)}`
                    },
                    {
                      name: 'External',
                      value: `${roundOffAndFormatNumberWithCurrency(effectiveInvestmentData?.investmentDetails?.externalInvestmentAmount?.amount ?? 0)}`
                    }
                  ]}
                  gap={7}
                  groupForAccessibility
                />
              </InPlaceNameValueAccordion>
            </div>
          ) : (
            <NameValueTableRow
              name='Invested'
              value={`${roundOffAndFormatNumberWithCurrency(totalInvested)}`}
              nameClassName='!text-[#3D3D3D]'
              valueClassName='!text-[#232323]'
              groupForAccessibility
            />
          )}
        </div>
        <NameValueTable
          data={returnsTableData}
          gap={9}
          nameClassName='!text-[#3D3D3D]'
          valueClassName='!text-[#232323]'
          groupForAccessibility
        />

        <hr aria-hidden='true' className='my-3 border-t border-[#EBF2F2]' />
        <NameValueTable
          data={[
            {
              name: 'Average NAV',
              value: `${fixedDigitsAndFormatNumberWithCurrency(effectiveInvestmentData?.averageNav?.amount ?? 0, 2)}`
            },
            {
              name: 'Total Units',
              value: `${toFixed(effectiveInvestmentData?.balancedUnits ?? 0, 2)}`
            },
            {
              name: 'First Investment Date',
              value: `${convertEpochToDate(effectiveInvestmentData?.firstInvestmentTimeStamp ?? Date.now(), '%_d %b %Y')}`
            }
          ]}
          gap={9}
          nameClassName='!text-[#3D3D3D]'
          valueClassName='!text-[#232323]'
          groupForAccessibility
        />
        {effectiveInvestmentData?.folioDetails && (
          <div className='pt-[9px] flex justify-between items-center w-full text-13'>
            {effectiveInvestmentData?.folioDetails?.length > 1 ? (
              <button
                type='button'
                aria-label='Folio Numbers, double tap to view folio details'
                onClick={() =>
                  openBottomSheet(
                    <FolioBottomSheet
                      folios={effectiveInvestmentData?.folioDetails}
                      onDismiss={closeBottomSheet}
                      ScreenName={INVESTMENT_PERFORMANCE_SCREEN_NAME}
                    />
                  )
                }
                className='text-[#3D3D3D] underline bg-transparent border-none p-0 cursor-pointer text-left'
              >
                Folio Numbers
              </button>
            ) : (
              <NameValueTableRow
                name='Folio Number'
                value={String(singleFolio ?? '')}
                groupForAccessibility
                accessibilityLabel={`Folio Number, ${formatDigitsForSpeech(singleFolio)}`}
                nameClassName='!text-[#6A6E6E] !text-[13px]'
                valueClassName='!text-black !font-[500] !text-[13px]'
                nameValueRowClassName='w-full'
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function formatDisplayPercentageValue({
  value,
  unit
}: {
  value: number
  unit: `${UNITS.PERCENTAGE}`
}) {
  if (!Number.isFinite(value)) {
    return convertUnitAndValue({ value: 0, unit })
  }

  const fixedDigits = isDecimal(value) ? 2 : 0
  const normalizedValue = Number(value.toFixed(fixedDigits)) === 0 ? 0 : value

  return convertUnitAndValue({
    value: normalizedValue,
    unit
  })
}

function formatXirrValue(xirr: PercentageReturn | null | undefined) {
  if (!xirr?.value) {
    return 'NA'
  }
  return formatDisplayPercentageValue({
    value: xirr?.value ?? 0,
    unit: xirr?.unit ?? UNITS.PERCENTAGE
  })
}

export function buildReturnsTableData({
  effectiveInvestmentData,
  xirrToggleOption,
  onXirrToggleClick
}: {
  effectiveInvestmentData: InvestmentDataType
  xirrToggleOption: string
  onXirrToggleClick: (option: string) => void
}): NameValueDatum[] {
  const hasLifeTimeXirr =
    effectiveInvestmentData?.lifeTimeXirr !== undefined && effectiveInvestmentData?.lifeTimeXirr !== null
  const activeXirr =
    hasLifeTimeXirr && xirrToggleOption === XIRR_TOGGLE_OPTIONS[1]
      ? effectiveInvestmentData?.lifeTimeXirr
      : effectiveInvestmentData?.xirr

  const xirrRow = hasLifeTimeXirr
    ? {
      name: '',
      value: formatXirrValue(activeXirr),
      nameContent: (
        <TextToggle
          currentToggleOption={xirrToggleOption}
          toggleOptions={XIRR_TOGGLE_OPTIONS}
          onToggleClick={onXirrToggleClick}
          toggleColor='#3D3D3D'
        />
      ),
      valueClassName: `${getChangeStyle(activeXirr?.value ?? 0)?.className ?? ''}`
    }
    : {
      name: 'XIRR',
      value: formatXirrValue(effectiveInvestmentData?.xirr),
      valueClassName: `${getChangeStyle(effectiveInvestmentData?.xirr?.value ?? 0)?.className ?? ''}`
    }

  return [
    {
      name: 'Total Returns',
      value: `${getChangeStyle(effectiveInvestmentData?.fundReturns?.returns?.value ?? 0)?.sign} ${roundOffAndFormatNumberWithCurrency(effectiveInvestmentData?.fundReturns?.fundAmount?.amount ?? 0)} 
                (${formatDisplayPercentageValue({
        value: effectiveInvestmentData?.fundReturns?.returns?.value ?? 0,
        unit: effectiveInvestmentData?.fundReturns?.returns?.unit ?? UNITS.PERCENTAGE
      })})`,
      valueClassName: `${getChangeStyle(effectiveInvestmentData?.fundReturns?.returns?.value ?? 0)?.className}`
    },
    {
      name: '1D Returns',
      value: `${getChangeStyle(effectiveInvestmentData?.oneDayReturns?.returns?.value ?? 0)?.sign} ${fixedDigitsAndFormatNumberWithCurrency(effectiveInvestmentData?.oneDayReturns?.fundAmount?.amount ?? 0, 2)} 
               (${formatDisplayPercentageValue({
        value: effectiveInvestmentData?.oneDayReturns?.returns?.value ?? 0,
        unit: effectiveInvestmentData?.oneDayReturns?.returns?.unit ?? UNITS.PERCENTAGE
      })})`,
      valueClassName: `${getChangeStyle(effectiveInvestmentData?.oneDayReturns?.returns?.value ?? 0)?.className}`
    },
    xirrRow
  ]
}
